import { Helmet } from "react-helmet-async";
import { Card, CardBody, Col, Container, FormGroup, Label, Row, Spinner } from "reactstrap";
import useQueryParams from "../../customHooks/useQueryParams";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ButtonCustom from "../../components/Buttons/ButtomCustom";
import { useForm } from "react-hook-form";
import { fieldsMessage } from "../../helpers/utils/fieldsMessage";
import ContentSnippet from "../../components/ContentSnippet/ContentSnippet";
import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { cleanIdSnippetDuplicate, cleanMessageSnippet, duplicateSnippet, getSnippet, getSnippetHistory, getSnippetPreview, loadEmptySnippet, saveSnippet } from "../../store/actions";
import { setValuesHookForm } from "../../helpers/load_object_hook_form";
import CheckboxField from "../../components/CheckboxField/CheckboxField";
import LabelWithHelp from "../../components/LabelWithHelp/LabelWithHelp";
import { useNavigate } from "react-router-dom";
import stringify from "json-stable-stringify";
import {openHtmlAsPdfPreview} from "../../helpers/utils/openHtmlAsPdfPreview";
import { buildSnapshot } from "../../helpers/utils/buildSnapshot";
import useAlert from "../../customHooks/useAlert";
import { useContextActions } from "../../customHooks/useContextActions";
import ActionButtons from "../../components/ActionButtons/ActionButtons";

const SnippetDetail = ({
	getSnippetRequest,
	getSnippetHistoryRequest,
	getSnippetPreviewRequest,
	postSnippetDuplicateRequest,
	snippet,
	snippetPreview,
	saveSnippetRequest,
	isSnippetSave,
	snippetIdDuplicate,
	loadingSnippet,
	loadEmptySnippet,
	messageSnippet,
	cleanMessageSnippet,
	cleanIdSnippetDuplicate,
	errorLoadingSnippet,
	errorLoadingSnippetHistory,
	errorSnippetPreview,
	errorSnippetDuplicate,
}) => {
	const [content, setContent] = useState([]);
	const [contentError, setContentError] = useState(null);
	const { 
		register, 
		handleSubmit, 
		formState: { errors },
		setValue,
		getValues,
	} = useForm({
		defaultValues: {
			active: true
		}
	});

	const initialHasRef = useRef(null);
	const saveActionRef = useRef(null);
	const titlePreviewRef = useRef("");

	const { confirmAlert, successAlert, errorAlert, okAlert } = useAlert();
	
	const alertModal = withReactContent(Swal);
	const navigate = useNavigate();
	const params = useQueryParams();
	const id = params.get("id");
	const idHistory = params.get("idHistory");
	
	function handleBack() {
		loadEmptySnippet();
		navigate("/snippets");
	}
	async function handleSave(values) {
		saveActionRef.current = null;
    
		const isValidContent = validateContent();
		if (!isValidContent) {
		setContentError(true);
		return;
		}
		setContentError(false);

		const isEdit = id !== null;
		const baseBody = {
			title: values.title,
			description: values.description,
			active: values.active,
			content,
		}
		const body = isEdit ? { id, ...baseBody } : baseBody;

		if(isEdit) {
			const result = await confirmAlert("Attention! All documents associated with this Snippet will be updated to Draft status. Do you want to continue?")
		
			if(!result.isConfirmed) return;
		
		}

		await saveSnippetRequest({
			body,
			method: isEdit ? "PUT" : "POST",
		});
	}
	function validateContent() {
	if (content.length === 0) return false;

		return content.every(block => {
			if (!block.content) return false;

			if (typeof block.content === "string") {
				return block.content.trim() !== "";
			}

			if (Array.isArray(block.content)) {
				return block.content.length > 0;
			}

			return true;
		});
	}
	function handleDuplicate() {
		if (id === null) return;

		const isValidContent = validateContent();
		if (!isValidContent) {
			setContentError(true);
			return;
		}
		setContentError(false);

		const values = getValues();

		const currentSnapshot = buildSnapshot({
			values,
			content,
		});

		if (stringify(initialHasRef.current) === stringify(currentSnapshot)) {
			saveActionRef.current = "DUPLICATE_ONLY";
			postSnippetDuplicateRequest({ snippetId: id });
			return;
		} else {
			confirmAlert(
				"You have unsaved changes. Do you want to save before duplicating?"
			).then((result) => {
				if (result.isConfirmed) {
					saveActionRef.current = "SAVE_AND_DUPLICATE";
					saveSnippetRequest({
						body: {
							id,
							...currentSnapshot,
						},
						method: "PUT",
					});
				}
			});
		}

		
	}
	function handlePreview() {
		if (!id) return;

		const isValidContent = validateContent();
		if (!isValidContent) {
			setContentError(true);
			return;
		}
		setContentError(false);
		
		const values = getValues();
		titlePreviewRef.current = values.title ?? "Snippet Preview";
		const currentSnapshot = buildSnapshot({
			values,
			content,
		})
		
		if (stringify(initialHasRef.current) === stringify(currentSnapshot)) {
			saveActionRef.current = "PREVIEW_ONLY";
			getSnippetPreviewRequest({ snippetId: id });
			return;

		} else {
			confirmAlert(
				"You have unsaved changes. Do you want to save before previewing?",
			).then((result) => {
				if (result.isConfirmed) {
					saveActionRef.current = "SAVE_AND_PREVIEW";
					saveSnippetRequest({
						body: {
							id: id,
							...currentSnapshot
						},
						method: "PUT"
					});
				} 

				return;
			})
		}
	}
	
	const actions = useContextActions({
		context: "snippet",
		screen: "detail",
	})
	const editableWithSave = actions.some(e => e === "save")
	
	useEffect(() => {
		if (id !== null) {
			getSnippetRequest({ snippetId: id });
		} else if (idHistory !== null) {
			getSnippetHistoryRequest({ snippetId: idHistory });
		} else {
			loadEmptySnippet();
		}

		return () => {
			cleanMessageSnippet();
			loadEmptySnippet();
			cleanIdSnippetDuplicate();
		}
		// eslint-disable-next-line
	}, [id])
	useEffect(() => {
		if (isSnippetSave !== null) {
			if (isSnippetSave) {
				if(saveActionRef.current === "SAVE_AND_PREVIEW") {
					okAlert(
						'Snippet saved successfully!',
					)
					.then((result) => {
						if (result.isConfirmed) {
							cleanMessageSnippet();
							getSnippetPreviewRequest({ snippetId: id });
						} 
						if (result.isDismissed) {
							cleanMessageSnippet();
						}
						saveActionRef.current = null;
						initialHasRef.current = buildSnapshot({
							values: getValues(),
							content,
						});
					});
					return; 
				}
				successAlert(
					`Snippet ${id === null ? "created" : "updated"} successfully!`,
					{
						didClose: () => {
							if (saveActionRef.current === "SAVE_AND_PREVIEW") {
								cleanMessageSnippet();
								getSnippetPreviewRequest({ snippetId: id });
								initialHasRef.current = buildSnapshot({
									values: getValues(),
									content,
								});
								saveActionRef.current = null;
							} else if (saveActionRef.current === "SAVE_AND_DUPLICATE") {
								cleanMessageSnippet();
								postSnippetDuplicateRequest({ snippetId: id });
								initialHasRef.current = buildSnapshot({
									values: getValues(),
									content,
								});
								saveActionRef.current = null;
							} else {
								cleanMessageSnippet();
								loadEmptySnippet();
								navigate("/snippets");
							}
						},
					}
				);
			} else {
				errorAlert(
					messageSnippet ?? "Something wrong, try again later.",
					{
						didClose: () => {
							cleanMessageSnippet();
							loadEmptySnippet();
						},
					}
				);
			}
		}
		// eslint-disable-next-line
	}, [isSnippetSave]);
	useEffect(() => {
		if (errorLoadingSnippet !== null) {
			alertModal
				.fire({
					title: "Something wrong, try again later.",
					icon: "error",
					showConfirmButton: false,
					showCloseButton: true,
					allowOutsideClick: false,
					//timer: 2000,
					//timerProgressBar: true,
				}).then((result) => {
					if (errorLoadingSnippet) {
						if (result.dismiss) {
							cleanMessageSnippet();
							loadEmptySnippet();
							navigate("/snippets");
						}
					}
					if (result.dismiss) {
						cleanMessageSnippet();
						loadEmptySnippet();
					}
				})
		}

		// eslint-disable-next-line
	}, [errorLoadingSnippet]);
	useEffect(() => {
		if (errorLoadingSnippetHistory !== null) {
			alertModal
				.fire({
					title: "Something wrong, try again later.",
					icon: "error",
					showConfirmButton: false,
					showCloseButton: true,
					allowOutsideClick: false,
					//timer: 2000,
					//timerProgressBar: true,
				}).then((result) => {
					if (errorLoadingSnippetHistory) {
						if (result.dismiss) {
							cleanMessageSnippet();
							loadEmptySnippet();
							navigate("/snippets");
						}
					}
					if (result.dismiss) {
						cleanMessageSnippet();
						loadEmptySnippet();
					}
				})
		}

		// eslint-disable-next-line
	}, [errorLoadingSnippetHistory]);
	useEffect(() => {
		if (snippetIdDuplicate !== null) {
			if (snippetIdDuplicate) {
				successAlert(
					"Snippet duplicated successfully!",
					{
						text: "You will be redirected to the copied snippet!",
						timer: undefined,
						showConfirmButton: true,
						customClass: {
							confirmButton: "btn btn-braskemPrimary btn-lg px-5",
						},
						didClose: () => {
							cleanIdSnippetDuplicate();
							loadEmptySnippet();
							navigate("/snippet?id=" + snippetIdDuplicate);
						},
					}
				);
				return;
			} 
			if (errorSnippetDuplicate) {
				errorAlert(
					errorSnippetDuplicate ? errorSnippetDuplicate : "Try again later!",
					{
						didClose: () => {
							cleanIdSnippetDuplicate();
							loadEmptySnippet();
						},
					}
				);
			}
		}  
		// eslint-disable-next-line
	}, [snippetIdDuplicate])
	useEffect(() => {
		if(!snippet) return;
		
		setValuesHookForm(snippet, setValue);
		setValue("active", snippet.active ?? true);

		let normalizeContent = [];
		
		if (Array.isArray(snippet.content)) {
			normalizeContent = snippet.content.map(block => ({
				id: crypto.randomUUID(),
				type: block.type,
				content: block.content,
				idStyle: block.idStyle ?? null,
				order: block.order, 
			}));
			
			setContent(normalizeContent);
		}

		initialHasRef.current = buildSnapshot({
			values: {
				title: snippet.title,
				description: snippet.description,
				active: snippet.active,
			},
			content: normalizeContent,
		});

		// eslint-disable-next-line
	},[snippet])
	useEffect(() => {
		if (contentError) {
			const isValid = validateContent();
			if (isValid) {
				setContentError(false);
			}
		}
	// eslint-disable-next-line
	}, [content]);
	useEffect(() => {
		if (snippetPreview) {
			openHtmlAsPdfPreview({
				html: snippetPreview.html,
				title: titlePreviewRef.current || "Snippet Preview",
			});
			return
		}
		if(errorSnippetPreview) {
			saveActionRef.current = null;
			errorAlert(
				errorSnippetPreview ? errorSnippetPreview : "Try again later!",
				{
					didClose: () => {
						cleanMessageSnippet();
						loadEmptySnippet();
					},
				}
			);
		}

		return () => {
			loadEmptySnippet();
			saveActionRef.current = null;
		}
		// eslint-disable-next-line
	}, [snippetPreview]);

	return (
		<div className="page-content">
			<Helmet>
				<title>Snippet</title>
			</Helmet>
			<Container fluid>
				<Row>
					<Col md={12}>
						<Card>
							<CardBody>
								<div>
									<Row className="my-2">
										{loadingSnippet ? (
											<div
												className="d-flex align-items-center justify-content-center"
												style={{ height: "400px", width: "100%" }}
											>
												<Spinner
													color="primary"
													style={{ height: "5rem", width: "5rem" }}
												/>
											</div>
										) : (
											<form onSubmit={handleSubmit(handleSave)}>
												<div className="d-flex justify-content-between my-2">
													<h2>{id === null 
														? idHistory === null 
														? "CREATE" 
														: "VIEW" 
														: "EDIT"}{" "}
														SNIPPET
													</h2>
													<div className="d-flex gap-2 mb-4">
														<ButtonCustom
															type="cancel"
															onClick={() => handleBack()}
														/>
														<ActionButtons 
															actions={actions}
															handlers={{
																preview: handleSubmit(handlePreview),
																save: handleSubmit(handleSave),
																duplicate: handleSubmit(handleDuplicate),
															}}
															context="snippet"
														/>
													</div>
												</div>
												<Row>
														<Col md="6">
															<FormGroup className="mb-3">
																<Label
																	htmlFor="title"
																	className="label-detail-form"
																>
																	Title
																</Label>
																<input
																	className="form-control"
																	id="title"
																	type="text"
																	disabled={(idHistory !== null) || (!editableWithSave)}
																	style={(idHistory !== null) || (!editableWithSave) ? {color: "#00000060"} : {}}
																	placeholder="Snippet title"
																	{...register("title", {
																		required: fieldsMessage.required("Snippet title"),
																		validate: (value) => value.trim() !== "" || fieldsMessage.required("Snippet title"),
																		maxLength: {
																			value: 100,
																			message: fieldsMessage.maxLength("Snippet title", 100),
																		}
																	})}
																/>
																<p className="error-form">
																	{errors.title?.message}
																</p>
															</FormGroup>
														</Col>
														<Col md="3">
															<Label className="label-detail-form">Created By</Label>
															<input 
															className="form-control"
															id="createUser"
															{...register("createUser")}
															disabled
															style={{ color: '#00000060'}}
															/>
														</Col>
														<Col md="3">
															<FormGroup className="mb-3">
																<Label
																	id="createDate"
																	className="label-detail-form"
																>
																	Creation Date
																</Label>
																<input
																	className="form-control disabled"
																	name="createDate"
																	{...register("createDate")}
																	disabled
																	style={{ color: '#00000060'}}
																/>
															</FormGroup>
														</Col>
														
												</Row>
												<Row>
													<Col md="6">
														<FormGroup className="mb-3">
															<Label
																id="description"
																className="label-detail-form"
															>
																Description
															</Label>
															<input
																className="form-control"
																name="description"
																type="text"
																disabled={(idHistory !== null) || (!editableWithSave)}
																style={(idHistory !== null) || (!editableWithSave) ? {color: "#00000060"} : {}}
																placeholder="Snippet description"
																{...register("description")}
															/>
														</FormGroup>
													</Col>
													<Col md="3">
														<Label className="label-detail-form">Updated By</Label>
														<input 
															className="form-control"
															id="updateUser"
															{...register("updateUser")}
															disabled
															style={{ color: '#00000060'}}
														/>
													</Col>	
													<Col md="3">
														<FormGroup className="mb-3">
															<Label
																id="updateDate"
																className="label-detail-form"
															>
																Update Date
															</Label>
															<input
																className="form-control"
																name="updateDate"
																{...register("updateDate")}
																disabled
																style={{ color: '#00000060'}}
															/>
														</FormGroup>
													</Col>
												</Row>
												
												<Row>
													<Col md="12">
														<hr />
													</Col>
												</Row>
												<Row>
													<Col md="12">
														<FormGroup className="mb-3">
															<Label
																id="snippet"
																className="label-detail-form"
															>
																Snippet Content {contentError && <span className="text-danger mb-2" style={{ fontSize: "12px" }}>(Please add at least one block with content before saving.)</span>}
															</Label>
															<ContentSnippet 
																value={content}
																onChange={setContent} 
																hasError={contentError}
																editable={!!idHistory || !editableWithSave}
															/>
														</FormGroup>
													</Col>
												</Row>
												<Row>
													<Col md="12">
														<div className="d-flex align-items-center gap-2 mt-4">
															<CheckboxField 
																id="active"
																name="active"
																register={register}
																editable={(idHistory !== null ? true : false) || (!editableWithSave)}
															/>
															<LabelWithHelp
																htmlFor="active"
																label="Active"
																typeMessage="active"
																entity="snippet"
																className="mb-0"
															/>
																
														</div>
													</Col>
												</Row>
												<div className="d-flex justify-content-end my-4 gap-2">
													<ButtonCustom
														type="cancel"
														onClick={() => handleBack()}
													/>
													<ActionButtons 
														actions={actions}
														handlers={{
															preview: handleSubmit(handlePreview),
															save: handleSubmit(handleSave),
														}}
														context="snippet"
													/>
												</div>
											</form>
										)}
									</Row>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</div>
	);
};

const mapStateToProps = ({
	SnippetsReducer: { loadingSnippet, messageSnippet, isSnippetSave, snippet, snippetIdDuplicate, errorLoadingSnippet, errorLoadingSnippetHistory, snippetPreview, errorSnippetPreview, errorSnippetDuplicate },
}) => ({
	loadingSnippet,
	messageSnippet,
	isSnippetSave,
	snippet,
	snippetIdDuplicate,
	snippetPreview,
	
	errorLoadingSnippet,
	errorLoadingSnippetHistory,
	errorSnippetPreview,
	errorSnippetDuplicate,

})

const mapDispatchToProps = {
	saveSnippetRequest: saveSnippet.request,
	getSnippetRequest: getSnippet.request,
	postSnippetDuplicateRequest: duplicateSnippet.request,
	getSnippetHistoryRequest: getSnippetHistory.request,
	getSnippetPreviewRequest: getSnippetPreview.request,
	cleanMessageSnippet,
	loadEmptySnippet,
	cleanIdSnippetDuplicate,
}

export default connect(mapStateToProps, mapDispatchToProps)(SnippetDetail);